import React, { useEffect, useMemo, useState } from 'react';
import { useTheme, Button } from 'tamagui';
import dayjs from 'dayjs';

import {
  CobuStack,
  CobuText,
  CobuButton,
  CobuCard,
  CobuGradientButton,
} from '@cobu/ui';
import { Strings } from '../../constants/Strings';
import { Gathering } from '@cobu/types';
import { useNavigate } from 'react-router-dom';
import { showErrorMessage } from '../../utils/messageToast';

interface CheckInModalProps {
  visible: boolean;
  onClose: () => void;
  gathering: Gathering;
  maxGuests?: number; // additional guests allowed per RSVP (0..n)
  currentTotalAttendees?: number; // existing RSVP attendees total (self + guests)
  onConfirm: (totalAttendees: number) => void | Promise<void>; // returns new total attendees
  onCancelRSVP?: () => void | Promise<void>; // when user chooses to cancel RSVP from modal
  hasExistingRSVP?: boolean; // if true, show update + cancel buttons
  isSubmitting?: boolean;
  hideGuestSection?: boolean;
}

// Commit type mapping aligned with EventDetailFooter
const COMMIT_TYPES = {
  RSVP: 'e8a642e9-7fe0-4f5a-9279-4cec3db10dd2',
  INTERESTED: '45890325-4e1d-4364-8aea-a13b2cf4c02c',
  REGISTER: 'b4a735d1-581b-4321-85a9-818ce6b2ff0a',
} as const;

export default function CheckInModal({
  visible,
  onClose,
  gathering,
  maxGuests,
  currentTotalAttendees,
  onConfirm,
  onCancelRSVP,
  hasExistingRSVP,
  isSubmitting,
  hideGuestSection = false,
}: CheckInModalProps) {
  const strings = Strings.get();
  const theme = useTheme();
  const navigate = useNavigate();
  const initialCount = useMemo(() => {
    // default to 1 (just you). If existing RSVP provided, keep that value.
    return Math.max(1, currentTotalAttendees ?? 1);
  }, [currentTotalAttendees]);

  const [totalAttendees, setTotalAttendees] = useState<number>(initialCount);
  const [isLocalSubmitting, setIsLocalSubmitting] = useState(false);
  const isActionInProgress = Boolean(isSubmitting || isLocalSubmitting);

  useEffect(() => {
    if (visible) {
      setTotalAttendees(initialCount);
    }
  }, [visible, initialCount]);

  const canHaveGuests = (maxGuests ?? 0) !== 0;
  const isStarted = useMemo(
    () => dayjs().isAfter(gathering.scheduledStartTime),
    [gathering.scheduledStartTime]
  );

  const handleDecrease = () => {
    if (isActionInProgress) return;
    setTotalAttendees(prev => Math.max(1, prev - 1));
  };

  const handleIncrease = () => {
    if (isActionInProgress) return;
    // totalAttendees = you (1) + additional guests
    if (!canHaveGuests) return;
    const additionalGuests = totalAttendees - 1;
    const maxAdditional = typeof maxGuests === 'number' ? maxGuests : Number.POSITIVE_INFINITY;
    if (additionalGuests < maxAdditional) {
      setTotalAttendees(prev => prev + 1);
    }
  };

  const handleConfirm = async () => {
    if (isActionInProgress) return;

    if (totalAttendees < 1) {
      showErrorMessage(`${strings.events.detail.alerts.invalidCount}: ${strings.events.detail.alerts.invalidCountMessage}`);    
      return;
    }

    try {
      setIsLocalSubmitting(true);
      await onConfirm(totalAttendees);
      onClose();
      // Open external registration when commit type requires it
      if (gathering.commitType === COMMIT_TYPES.REGISTER && (gathering as any)?.registrationURL) {
        const url = (gathering as any).registrationURL as string;
        if (url && typeof url === 'string') {
          navigate(`/webview?url=${url}&title=${strings.events.detail.buttons.register}`);
        }
      }
    } finally {
      setIsLocalSubmitting(false);
    }
  };

  const headerTitle = useMemo(() => {
    switch (gathering.commitType) {
      case COMMIT_TYPES.REGISTER:
        return strings.events.detail.buttons.register;
      case COMMIT_TYPES.INTERESTED:
        return strings.events.detail.buttons.expressInterest;
      default:
        return strings.events.detail.buttons.rsvp;
    }
  }, [gathering.commitType, strings]);

  const confirmPrimaryText = useMemo(() => {
    if (hasExistingRSVP) {
      return gathering.commitType === COMMIT_TYPES.REGISTER
        ? strings.events.detail.buttons.updateRegistration
        : strings.events.detail.buttons.updateRsvp;
    }
    if (isStarted) {
      return strings.events.detail.buttons.confirmCheckin;
    }
    return gathering.commitType === COMMIT_TYPES.REGISTER
      ? strings.events.detail.buttons.confirmRegistration
      : strings.events.detail.buttons.completeRsvp;
  }, [hasExistingRSVP, isStarted, gathering.commitType]);

  const handleCancelRsvp = async () => {
    if (isActionInProgress) return;

    setIsLocalSubmitting(true);
    if (onCancelRSVP) {
      try {
        await onCancelRSVP();
        onClose();
      } finally {
        setIsLocalSubmitting(false);
      }
      return;
    }

    setIsLocalSubmitting(false);
    onClose();
  };

  if (!visible) return null;

  return (
      <div style={{ position: 'fixed', inset: 0, zIndex: 100, background: 'rgba(0,0,0,0.4)' }}>
        <CobuStack w="100%" h="100%" bg="$background">
          {/* Header */}
          <CobuStack bg="$cobuDarkBlue" px="$4" py="$3">
            <CobuStack layout="horizontal" ai="center" jc="space-between">
              <CobuButton backgroundColor="transparent" onPress={onClose}>
                <CobuText color="$white" fos="$4">×</CobuText>
              </CobuButton>
 
              <CobuText variant="heading" color="$white" fos="$4" fow="bold" ta="center">
                {headerTitle}
              </CobuText>
              <CobuStack w="$10" />
            </CobuStack>
          </CobuStack>
 
          {/* Content */}
          <CobuStack f={1} jc="center" ai="center" px="$4" gap="$5" style={{ overflowY: 'auto' }}>
          {/* Greeting */}
          <CobuText variant="heading" fos="$6" ta="center">
            {strings.events.detail.labels.checkInGreetingHeading}
          </CobuText>

          {!hideGuestSection && canHaveGuests && (
            <CobuText variant="heading" fos="$4" fow="$medium" ls={0.9} ta="center">
              {isStarted
                ? strings.events.detail.labels.guestsWithYouQuestion
                : strings.events.detail.labels.bringingGuestsQuestion}
            </CobuText>
          )}

          {!hideGuestSection && canHaveGuests && (
            <CobuStack layout="horizontal" ai="center" jc="center" gap="$4">
              <CobuStack
                onPress={totalAttendees <= 1 || isActionInProgress ? undefined : handleDecrease}
                ai="center"
                jc="center"
                w="$10"
                style={{
                  cursor: totalAttendees <= 1 || isActionInProgress ? 'not-allowed' : 'pointer',
                  opacity: totalAttendees <= 1 || isActionInProgress ? 0.5 : 1,
                  boxShadow: 'none',
                  elevation: 0,
                  WebkitTapHighlightColor: 'transparent',
                }}
              >
                <CobuText variant="body" fos="$6" fow="bold" ta="center">
                  -
                </CobuText>
              </CobuStack>
              <CobuText variant="body" fos="$6" fow="bold" ta="center" w="$10">
                {Math.max(0, totalAttendees - 1)}
              </CobuText>
              <CobuStack
                onPress={!canHaveGuests || isActionInProgress ? undefined : handleIncrease}
                ai="center"
                jc="center"
                w="$10"
                style={{
                  cursor: !canHaveGuests || isActionInProgress ? 'not-allowed' : 'pointer',
                  opacity: !canHaveGuests || isActionInProgress ? 0.5 : 1,
                  boxShadow: 'none',
                  elevation: 0,
                  WebkitTapHighlightColor: 'transparent',
                }}
              >
                <CobuText variant="body" fos="$6" fow="bold" ta="center">
                  {strings.events.detail.buttons.plus}
                </CobuText>
              </CobuStack>
            </CobuStack>
          )}

          {!hideGuestSection && canHaveGuests && (
            <CobuStack ai="center">
              <CobuText variant="body" fos="$2" color="$colorMuted">
                {`${strings.events.list.item.youAnd} ${Math.max(0, totalAttendees - 1)} ${Math.max(0, totalAttendees - 1) === 1 ? strings.events.detail.labels.guestSingular : strings.events.detail.labels.guestPlural}`}
              </CobuText>
            </CobuStack>
          )}

          {!hideGuestSection && !canHaveGuests && (
            <CobuCard p="$4" br="$3">
              <CobuText variant="body" ta="center">
                {strings.events.detail.labels.noMoreGuestsAllowed}
              </CobuText>
            </CobuCard>
          )}

          {/* Actions */}
          <CobuStack p="$4" w="100%">
            {hasExistingRSVP ? (
              <>
                <CobuButton
                  title={isActionInProgress ? strings.events.detail.buttons.updating : confirmPrimaryText}
                  variant="primary"
                  onPress={handleConfirm}
                  disabled={isActionInProgress}
                />
                <CobuStack ai="center" mt="$3">
                  {!!onCancelRSVP && (
                    <CobuStack role="button" tabIndex={0} onPress={isActionInProgress ? undefined : handleCancelRsvp} style={{ cursor: isActionInProgress ? 'not-allowed' : 'pointer', opacity: isActionInProgress ? 0.5 : 1 }}>
                      <CobuText variant="body" fow="700" col="$textLinkColor" ls={0.9}>
                        {(strings.events.detail.alerts.cannotAttend || '').toUpperCase()}
                      </CobuText>
                    </CobuStack>
                  )}
                </CobuStack>
              </>
            ) : (
              <CobuButton
                title={isActionInProgress ? strings.events.detail.buttons.updating : confirmPrimaryText}
                variant="primary"
                onPress={handleConfirm}
                disabled={isActionInProgress}
              />
            )}
          </CobuStack>
          </CobuStack>
        </CobuStack>
      </div>
  );
}
